import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * The order bump row (`cart-bump.liquid`): a sideways list of cards, each with
 * its own colour and size choice and an Add button. It lives in the cart and,
 * through the `product-bump` block, on the product page.
 *
 * Which products appear, and in what order, is decided in Liquid. This element
 * only resolves a card to a variant and adds it:
 *
 * - colour comes from the pressed swatch, or the only colour there is
 * - size is always the shopper's own choice: on anything sold in sizes, Add
 *   opens the card's size chips and a tap on one adds it
 *
 * Every add carries `_bump` (the mode), plus `_look`/`_look_id` in look mode, the
 * same properties the look page adds its pieces with. The re-rendered sections
 * then morph every cart component, and the added product drops out of the row.
 * The product page row is not a cart section, so there the card removes itself,
 * and the cart drawer opens on the add like it does for the main button.
 *
 * Listeners are delegated from this element, so the chips it creates itself and
 * the cards a cart morph swaps in need no wiring of their own.
 */
class CartBumpComponent extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.track?.addEventListener('scroll', this.#updateArrows, { passive: true });
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.track?.removeEventListener('scroll', this.#updateArrows);
  }

  get track() {
    return this.querySelector('.cart-bump__track');
  }

  /**
   * Parsed on every use rather than cached: a cart morph can reuse a card element
   * for a different product, and a cache on the element would outlive it.
   * @param {Element} card
   */
  #variants(card) {
    try {
      return JSON.parse(card.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  /** @param {Element} card */
  #colour(card) {
    const pressed = card.querySelector('.cart-bump__swatch[aria-pressed="true"]');
    if (pressed instanceof HTMLElement) return pressed.dataset.color;
    return this.#variants(card).find((v) => v.a)?.c ?? null;
  }

  /** Available variants of the card's current colour. @param {Element} card */
  #options(card) {
    const colour = this.#colour(card);
    return this.#variants(card).filter((v) => v.a && (colour === null || v.c === colour));
  }

  /**
   * The variant Add puts in the cart straight away, or undefined when the
   * product comes in sizes and she still has to pick one.
   * @param {Element} card
   */
  #resolved(card) {
    if (card.getAttribute('data-has-size') === 'true') return undefined;
    return this.#options(card)[0];
  }

  /**
   * Repaints the card's photo, subtitle and price after a colour change. The first
   * paint comes from Liquid.
   * @param {Element} card
   */
  #sync(card) {
    const shown = this.#options(card)[0];

    const img = card.querySelector('[data-img]');
    if (img instanceof HTMLImageElement) {
      const src = shown?.i || img.dataset.default;
      if (src && img.getAttribute('src') !== src) img.src = src;
    }

    const sub = card.querySelector('[data-sub]');
    if (sub) sub.textContent = shown?.c ?? '';

    const price = card.querySelector('[data-price]');
    if (price && shown?.p) price.textContent = shown.p;

    const panel = card.querySelector('[data-sizes-panel]');
    if (panel instanceof HTMLElement) {
      panel.hidden = true;
      panel.replaceChildren();
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || this.#busy) return;

    const arrow = target.closest('.cart-bump__arrow');
    if (arrow instanceof HTMLElement) return this.#scroll(Number(arrow.dataset.dir));

    const card = target.closest('[data-card]');
    if (!card) return;

    const swatch = target.closest('.cart-bump__swatch');
    if (swatch instanceof HTMLButtonElement) {
      for (const other of card.querySelectorAll('.cart-bump__swatch')) {
        other.setAttribute('aria-pressed', String(other === swatch));
      }
      return this.#sync(card);
    }

    const chip = target.closest('.cart-bump__chip');
    if (chip instanceof HTMLElement) return this.#add(Number(chip.dataset.variantId));

    if (target.closest('[data-add]')) {
      const resolved = this.#resolved(card);
      if (resolved) return this.#add(resolved.id);
      this.#openSizes(card);
    }
  };

  /** Shows the card's sizes for its current colour, as chips. @param {Element} card */
  #openSizes(card) {
    const panel = card.querySelector('[data-sizes-panel]');
    if (!(panel instanceof HTMLElement)) return;

    if (!panel.hidden) {
      panel.hidden = true;
      return;
    }

    panel.replaceChildren(
      ...this.#options(card).map((variant) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'button-unstyled cart-bump__chip';
        chip.dataset.variantId = String(variant.id);
        chip.textContent = variant.s ?? '';
        chip.setAttribute('aria-label', `Add size ${variant.s}`);
        return chip;
      })
    );
    panel.hidden = false;
    panel.querySelector('button')?.focus();
  }

  /**
   * Takes an added card out of the product page row, and the row with it once
   * nothing is left to offer.
   * @param {Element | undefined} card
   */
  #drop(card) {
    card?.remove();
    if (!this.querySelector('[data-card]')) {
      this.hidden = true;
      return;
    }
    if (this.querySelectorAll('[data-card]').length === 1) {
      this.querySelector('.cart-bump__arrows')?.remove();
    }
    this.#updateArrows();
  }

  /** @param {number} dir */
  #scroll(dir) {
    const track = this.track;
    const card = track?.querySelector('[data-card]');
    if (!track || !(card instanceof HTMLElement)) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: dir * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  #updateArrows = () => {
    const track = this.track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 1;
    for (const arrow of this.querySelectorAll('.cart-bump__arrow')) {
      if (!(arrow instanceof HTMLButtonElement)) continue;
      arrow.disabled = Number(arrow.dataset.dir) < 0 ? track.scrollLeft <= 0 : track.scrollLeft >= max;
    }
  };

  /** @param {number} variantId */
  #add(variantId) {
    if (this.#busy || !variantId) return;
    this.#busy = true;
    this.classList.add('cart-bump--busy');

    const card = [...this.querySelectorAll('[data-card]')].find((el) =>
      this.#variants(el).some((variant) => variant.id === variantId)
    );

    /** @type {Record<string, string>} */
    const properties = { _bump: this.dataset.mode || 'pair' };
    if (this.dataset.lookTitle) properties._look = this.dataset.lookTitle;
    if (this.dataset.lookId) properties._look_id = this.dataset.lookId;

    const sections = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) sections.add(component.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties }],
          sections: [...sections].join(','),
          sections_url: window.location.pathname,
        }),
      })
    )
      .then((response) => response.json())
      .then(async (data) => {
        if (data.status) throw new Error(data.description || data.message || 'Could not add the piece');

        // `/cart/add.js` answers with the added items, not the cart, so read the
        // cart back for the event payload. The sections are all the morph needs.
        const cart = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((response) => response.json());

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'cart-bump-component', items: cart.items, sections: data.sections, didError: false },
        });

        if (this.dataset.context === 'product') this.#drop(card);
      })
      .catch((error) => {
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Could not add the piece', code: 'SERVICE_UNAVAILABLE' })
        );
      })
      .finally(() => {
        this.#busy = false;
        this.classList.remove('cart-bump--busy');
      });
  }
}

if (!customElements.get('cart-bump-component')) {
  customElements.define('cart-bump-component', CartBumpComponent);
}
